// Public Global Master Auction leaderboard — shown on the landing page below the
// hero. Fully ANONYMIZED: only Country, Current High Bid, and Total Bidders.
// Includes a "Place Your Bid" modal that submits to the public auction endpoint.
import { useEffect, useState, useCallback, FormEvent } from 'react';
import { Crown, Trophy, Users, Globe2, Clock, X, Gavel, Loader2, CheckCircle2 } from 'lucide-react';
import {
  fetchLeaderboard, submitBid, formatUSD, countryFlag, daysUntilClose,
  type LeaderboardResponse, type BidInput,
} from '../../lib/auction';

const LEGACY_PRICE = 9999;

function BidModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }): JSX.Element {
  const [form, setForm] = useState<BidInput>({
    name: '', company: '', rcdd_id: '', email: '', country: '', bid_amount: LEGACY_PRICE,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function set<K extends keyof BidInput>(k: K, v: BidInput[K]): void {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await submitBid({ ...form, bid_amount: Number(form.bid_amount) });
      setDone(true);
      onSuccess();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Bid failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="bid-modal">
      <div className="w-full max-w-md rounded-xl border border-amber-300/30 bg-[#0c1017] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Gavel className="h-4 w-4 text-amber-300" />
            <span className="text-sm font-semibold text-amber-200 tracking-wide">Place Your Bid — National Master Seat</span>
          </div>
          <button data-testid="bid-modal-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-200 transition"><X className="h-4 w-4" /></button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="bid-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
            <div className="text-base font-semibold text-white">Bid submitted!</div>
            <p className="text-[12px] text-zinc-400 mt-2">Your bid for the {form.country} Master seat is now on the leaderboard. The founder will verify your RCDD credentials.</p>
            <button data-testid="bid-success-close" onClick={onClose} className="mt-5 h-10 px-6 rounded-sm bg-amber-300 text-zinc-900 font-semibold text-[13px] hover:bg-amber-200 transition">Done</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-5 space-y-3" data-testid="bid-form">
            {([
              ['name', 'Full Name', 'text', true],
              ['company', 'Company Name', 'text', false],
              ['rcdd_id', 'RCDD ID Number', 'text', true],
              ['email', 'Work Email', 'email', true],
              ['country', 'Country', 'text', true],
            ] as const).map(([key, label, type, req]) => (
              <div key={key}>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 block">{label}{req ? ' *' : ''}</label>
                <input
                  data-testid={`bid-${key}`}
                  type={type}
                  required={req}
                  value={form[key] as string}
                  onChange={(e) => set(key, e.target.value)}
                  className="w-full h-9 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-zinc-100 focus:outline-none focus:border-amber-300/60 transition"
                />
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 block">Bid Amount (USD) *</label>
              <input
                data-testid="bid-amount"
                type="number"
                min={1}
                step={1}
                required
                value={form.bid_amount}
                onChange={(e) => set('bid_amount', Number(e.target.value))}
                className="w-full h-9 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-amber-200 font-semibold focus:outline-none focus:border-amber-300/60 transition"
              />
              <p className="text-[10px] text-zinc-600 mt-1">Perpetual Legacy License base is {formatUSD(LEGACY_PRICE)}. One seat awarded per country on Oct 20.</p>
            </div>
            {err && <div data-testid="bid-error" className="px-3 py-2 rounded-sm border border-rose-900/60 bg-rose-950/40 text-rose-300 text-[12px]">{err}</div>}
            <button data-testid="bid-submit" type="submit" disabled={busy} className="w-full h-10 rounded-sm bg-amber-300 text-zinc-900 font-semibold text-[13px] hover:bg-amber-200 disabled:opacity-60 transition flex items-center justify-center gap-2">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Submit Bid</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function AuctionLeaderboard(): JSX.Element {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [showBid, setShowBid] = useState(false);
  const days = daysUntilClose();

  const load = useCallback(async () => {
    try { setData(await fetchLeaderboard()); } catch { /* keep last */ }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 20000);
    return () => clearInterval(t);
  }, [load]);

  const rows = data?.countries ?? [];

  return (
    <section data-testid="auction-leaderboard" className="w-full border-t border-zinc-800/80 bg-gradient-to-b from-[#080a0f] to-[#0b0e14] px-6 lg:px-16 py-14">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-amber-300/80 mb-2">
              <Crown className="h-3.5 w-3.5" /> Global Master Auction · Live
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
              One <span className="text-amber-300">National Master Exclusive</span> seat per country.
            </h2>
            <p className="text-[13px] text-zinc-400 mt-2 max-w-xl leading-relaxed">
              Bid for the perpetual {formatUSD(LEGACY_PRICE)} Legacy License reserved for your nation. Only one is awarded per country — winners announced <span className="text-zinc-200 font-medium">October 20</span>.
            </p>
          </div>
          <button
            data-testid="place-bid-btn"
            onClick={() => setShowBid(true)}
            className="shrink-0 h-11 px-6 rounded-full bg-amber-300 text-zinc-900 font-bold text-[13px] hover:bg-amber-200 transition flex items-center gap-2 shadow-lg shadow-amber-500/10"
          >
            <Gavel className="h-4 w-4" /> Place Your Bid
          </button>
        </div>

        {/* Summary chips */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { icon: Users, label: 'Total Bidders', value: data?.total_bidders ?? 0, accent: '#5eead4' },
            { icon: Globe2, label: 'Countries Competing', value: data?.total_countries ?? 0, accent: '#a5b4fc' },
            { icon: Clock, label: 'Days Remaining', value: days, accent: '#fcd34d' },
          ].map((c) => (
            <div key={c.label} className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
                <c.icon className="h-3.5 w-3.5" style={{ color: c.accent }} /> {c.label}
              </div>
              <div className="text-2xl font-bold" style={{ color: c.accent }} data-testid={`auction-stat-${(c.label.split(' ')[0] ?? '').toLowerCase()}`}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* Leaderboard table — ANONYMIZED */}
        <div className="rounded-xl border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm" data-testid="auction-public-table">
            <thead>
              <tr className="bg-zinc-900/70 text-zinc-400 text-[11px] uppercase tracking-[0.14em]">
                <th className="text-left px-4 py-3 font-medium">#</th>
                <th className="text-left px-4 py-3 font-medium">Country</th>
                <th className="text-right px-4 py-3 font-medium">Current High Bid</th>
                <th className="text-right px-4 py-3 font-medium">Total Bidders</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-600 text-[13px]" data-testid="auction-empty">No bids yet — be the first to claim your nation's Master seat.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.country} data-testid="auction-row" className="border-t border-zinc-800/70 hover:bg-zinc-900/40 transition">
                  <td className="px-4 py-3">
                    {i === 0 ? <Trophy className="h-4 w-4 text-amber-300" /> : <span className="text-zinc-500 font-mono">{i + 1}</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-100">
                    <span className="mr-2">{countryFlag(r.country)}</span>{r.country}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-amber-200">{formatUSD(r.high_bid)}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{r.total_bidders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10.5px] text-zinc-600 mt-3">Bidder identities are private. Only country, leading bid, and participation count are shown publicly.</p>
      </div>

      {showBid && <BidModal onClose={() => setShowBid(false)} onSuccess={() => void load()} />}
    </section>
  );
}
